import { Component, OnInit, Input, EventEmitter, Output, OnChanges, signal, TemplateRef, ViewChild, ElementRef, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowObjectDefinition, FlowObjectType, FormSchema } from '../../../../models/flow-object.model';
import { ConfigSchema, GatewayPathsCondition, GatewayRuleOperator, GatewayRuleOperatorDescription } from '../../../../models/config-schema.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from 'src/app/shared/enums';
import { Utils } from '../../../../shared/utils';
import { IBaseOption } from '../../../../models/base.model';
import { FlowObjectDefinitionService } from '../../../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'fod-gateway-paths',
    templateUrl: './fod-gateway-paths.component.html',
    styleUrl: './fod-gateway-paths.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})

export class FlowObjectDetailsGatewayPathsComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    GatewayRuleOperator = GatewayRuleOperator;
    DivisionMode = Enums.DivisionMode;
    // #endregion

    // #region [props]
    configSchema = new ConfigSchema();
    gatewayRuleOperatorOptions: IBaseOption[] = [];
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $firstGatewayPathName = signal(null as string);
    $secondGatewayPathName = signal(null as string);
    $divisionMode = signal(Enums.DivisionMode.Conditional);
    $isLogicalOperatorAnd = signal(false);
    $conditions = signal([new GatewayPathsCondition()]);
    $formFieldOptions = signal([] as IBaseOption[]);
    $configSchemaFlat = signal(null as string);
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        private flowObjectDefinitionService: FlowObjectDefinitionService
    ) {
        for (let item in GatewayRuleOperator) {
            if (
                !Number.isNaN(Number.parseInt(item))
                && Number.parseInt(item) != GatewayRuleOperator.IsNull
                && Number.parseInt(item) != GatewayRuleOperator.IsNotNull
            ) {
                this.gatewayRuleOperatorOptions.push({
                    value: +item,
                    description: GatewayRuleOperatorDescription.get(+item)
                });
            }
        }
    }

    async ngOnInit() {
        if (this.$model()?.configSchema != null) {
            this.configSchema = JSON.parse(this.$model().configSchema) as ConfigSchema;
            this.initFromModel();
        }

        let formFlowObject = this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formFlowObject != null && Utils.isNotNullOrEmpty(this._flowDefinition.ownerInfo)) {
            if (Utils.isNullOrEmpty(formFlowObject.formSchema)) {
                const response = await this.flowObjectDefinitionService.getFormSchema(formFlowObject.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                formFlowObject.formSchema = response.data;
            }

            let formSchema = JSON.parse(formFlowObject.formSchema) as FormSchema;
            let formFlowObjectSchemaFields = Utils.getFormFieldsWithUsableValue(formSchema.components);
            this.$formFieldOptions.set(formFlowObjectSchemaFields.map(x => ({ value: x.key, description: x.label })));
        }

        for (let item of this.$conditions()) {
            this.ruleOperatorChange(item);
        }
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    ruleOperatorChange(condition: GatewayPathsCondition) {
        if (condition.ruleOperator < GatewayRuleOperator.IsTrue || condition.ruleOperator > GatewayRuleOperator.IsNotEmpty) {
            condition.referenceValueDisabled = false;
        } else {
            condition.referenceValue = null;
            condition.referenceValueDisabled = true;
        }
    }

    getFormFieldDescription(condition: GatewayPathsCondition): string {
        return this.$formFieldOptions().find(x => x.value == condition.formField)?.description;
    }

    getRuleOperatorDescription(condition: GatewayPathsCondition): string {
        return this.gatewayRuleOperatorOptions.find(x => x.value == condition.ruleOperator)?.description;
    }

    divisionModeChange(event?) {
        if (event == null) return;
        this.$divisionMode.set(event);
    }

    addCondition() {
        Utils.updSignal(this.$conditions, x => {
            x.push(new GatewayPathsCondition());
            return x;
        });
    }

    removeCondition(condition: GatewayPathsCondition) {
        this.$conditions.set(this.$conditions().filter(x => x.timestamp != condition.timestamp));
    }

    showConfigSchema() {
        let tempObject = {
            model: JSON.parse(JSON.stringify(this.$model())) as FlowObjectDefinition,
            configSchema: JSON.parse(JSON.stringify(this.configSchema))
        };
        delete tempObject.model.configSchema;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual e se a origem não é um FlowDefinition
                if (modalObject.model.typeId != this.$model().typeId || modalObject.model.targetId != null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != JSON.stringify(modalObject)) {
                    this.configSchema = JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchema;
                    this.initFromModel();
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowObjectConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        });
    }

    onSubmit() {
        if (this.$isReadOnlyMode() || this.isInvalidConfiguration()) return false;

        if (this.$divisionMode() == Enums.DivisionMode.Deliberation) {
            this.$conditions.set([]);
        }

        this.configSchema.taskGatewayPaths.firstGatewayPathName = this.$firstGatewayPathName();
        this.configSchema.taskGatewayPaths.secondGatewayPathName = this.$secondGatewayPathName();
        this.configSchema.taskGatewayPaths.isDivisionByDeliberation = this.$divisionMode() == Enums.DivisionMode.Deliberation;
        this.configSchema.taskGatewayPaths.logicalOperator = this.$isLogicalOperatorAnd() ? 'and' : 'or';
        this.configSchema.taskGatewayPaths.conditions = this.$conditions();

        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.configSchema);
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion

    private initFromModel() {
        this.$firstGatewayPathName.set(this.configSchema.taskGatewayPaths.firstGatewayPathName);
        this.$secondGatewayPathName.set(this.configSchema.taskGatewayPaths.secondGatewayPathName);
        this.$divisionMode.set(this.configSchema.taskGatewayPaths.isDivisionByDeliberation ? Enums.DivisionMode.Deliberation : Enums.DivisionMode.Conditional);
        this.$isLogicalOperatorAnd.set(this.configSchema.taskGatewayPaths.logicalOperator == 'and');
        Utils.setSignal(this.$conditions, this.configSchema.taskGatewayPaths.conditions);
    }

    private isInvalidConfiguration() {
        if (this.$divisionMode() == Enums.DivisionMode.Conditional) {
            for (let condition of this.$conditions()) {
                if (
                    Utils.isNullOrEmpty(condition.formField)
                    || (
                        Utils.isNullOrEmpty(condition.referenceValue)
                        && (condition.ruleOperator < GatewayRuleOperator.IsTrue || condition.ruleOperator > GatewayRuleOperator.IsNotEmpty)
                    )
                ) {
                    this.toastr.warning(Enums.Messages.NullOrEmpty, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                    return true;
                }

                if (
                    condition.ruleOperator > GatewayRuleOperator.NotEqual
                    && condition.ruleOperator < GatewayRuleOperator.IsTrue
                    && Number.isNaN(Number(condition.referenceValue))
                ) {
                    this.toastr.warning(Enums.Messages.IsNotNumber, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                    return true;
                }
            }
        }

        return false;
    }
}
